"use client";

import { motion } from 'framer-motion';
import { FileText, Shield, Clock, CreditCard, RefreshCw, AlertTriangle, Gavel, Mail, Phone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useLanguage } from '@/contexts/language-context';

const AnimatedBackground = dynamic(() => import('@/components/animated-background'), { ssr: false });

const TermsPage = () => {
  const { t, language } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100
      }
    }
  };

  return (
    <div className="relative min-h-screen">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-cloud-blue to-cloud-lavender mb-6">
                <Gavel className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-deep-purple dark:text-cream mb-6">
              {language === 'nl' ? 'Algemene Voorwaarden' : 'Terms and Conditions'}
            </h1>
            <p className="text-xl text-deep-purple/80 dark:text-cream/80 mb-8 leading-relaxed max-w-3xl mx-auto">
              {language === 'nl' 
                ? 'Deze algemene voorwaarden zijn van toepassing op alle diensten en producten geleverd door Agnus Cloud. Door gebruik te maken van onze diensten gaat u akkoord met deze voorwaarden.'
                : 'These terms and conditions apply to all services and products delivered by Agnus Cloud. By using our services, you agree to these terms.'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-200 dark:border-red-700">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                <h2 className="text-2xl font-bold text-red-600">
                  {language === 'nl' ? 'Juridische Disclaimer' : 'Legal Disclaimer'}
                </h2>
              </div>
              <p className="text-deep-purple/80 dark:text-cream/80 leading-relaxed">
                {language === 'nl'
                  ? 'De informatie verstrekt door Agnus Cloud via onze website, platforms en andere communicatiemiddelen is uitsluitend bedoeld voor algemene informatieve doeleinden. Hoewel we ons uiterste best doen om de juistheid en actualiteit van de inhoud te waarborgen, kunnen we geen garantie bieden met betrekking tot de volledigheid, betrouwbaarheid, geschiktheid of beschikbaarheid van de informatie, producten, diensten of gerelateerde grafische elementen die worden aangeboden.'
                  : 'The information provided by Agnus Cloud through our website, platforms and other communication channels is intended solely for general informational purposes. While we strive to ensure the accuracy and timeliness of the content, we cannot guarantee the completeness, reliability, suitability or availability of the information, products, services or related graphic elements offered.'
                }
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Terms Content */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-12"
          >
            {/* 1. Definities */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500"
                    >
                      <FileText className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      1. {language === 'nl' ? 'Definities' : 'Definitions'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <p className="text-deep-purple/80 dark:text-cream/80">
                      {language === 'nl' ? 'In deze voorwaarden wordt verstaan onder:' : 'In these terms and conditions, the following definitions apply:'}
                    </p>
                    <ul className="space-y-3 text-deep-purple/70 dark:text-cream/70">
                      <li>
                        <strong className="text-deep-purple dark:text-cream">Agnus Cloud:</strong>{' '}
                        {language === 'nl' 
                          ? 'De aanbieder van IT-diensten en producten, gevestigd in Nederland.'
                          : 'The provider of IT services and products, based in the Netherlands.'
                        }
                      </li>
                      <li>
                        <strong className="text-deep-purple dark:text-cream">
                          {language === 'nl' ? 'Klant:' : 'Client:'}
                        </strong>{' '}
                        {language === 'nl'
                          ? 'De natuurlijke persoon of rechtspersoon die gebruik maakt van de diensten of producten van Agnus Cloud.'
                          : 'The natural person or legal entity that uses the services or products of Agnus Cloud.'
                        }
                      </li>
                      <li>
                        <strong className="text-deep-purple dark:text-cream">
                          {language === 'nl' ? 'Diensten:' : 'Services:'}
                        </strong>{' '}
                        {language === 'nl'
                          ? 'Alle door Agnus Cloud aangeboden producten, software, hosting, onderhoud en andere gerelateerde services.'
                          : 'All products, software, hosting, maintenance and other related services offered by Agnus Cloud.'
                        }
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 2. Aanbiedingen en overeenkomsten */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-green-500 to-emerald-500"
                    >
                      <Shield className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      2. {language === 'nl' ? 'Aanbiedingen en Overeenkomsten' : 'Offers and Agreements'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <ul className="space-y-3 text-deep-purple/70 dark:text-cream/70">
                      <li>
                        {language === 'nl'
                          ? 'Alle offertes en aanbiedingen van Agnus Cloud zijn vrijblijvend, tenzij anders vermeld.'
                          : 'All quotes and offers from Agnus Cloud are non-binding, unless stated otherwise.'
                        }
                      </li>
                      <li>
                        {language === 'nl'
                          ? 'Een overeenkomst komt tot stand nadat de klant een schriftelijke of mondelinge bevestiging heeft gegeven op een offerte of voorstel van Agnus Cloud.'
                          : 'An agreement is established after the client has given written or verbal confirmation to a quote or proposal from Agnus Cloud.'
                        }
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 3. Betalingen */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500"
                    >
                      <CreditCard className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      3. {language === 'nl' ? 'Betalingen' : 'Payments'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <ul className="space-y-3 text-deep-purple/70 dark:text-cream/70">
                      <li>
                        {language === 'nl'
                          ? 'Alle betalingen dienen te worden voldaan binnen 14 dagen na factuurdatum, tenzij anders overeengekomen.'
                          : 'All payments must be made within 14 days after the invoice date, unless otherwise agreed.'
                        }
                      </li>
                      <li>
                        {language === 'nl'
                          ? 'Bij late betalingen is Agnus Cloud gerechtigd rente in rekening te brengen conform de wettelijke rente, evenals eventuele incassokosten.'
                          : 'In case of late payments, Agnus Cloud is entitled to charge interest according to the statutory rate, as well as any collection costs.'
                        }
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 4. Levering van diensten */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-orange-500 to-red-500"
                    >
                      <Clock className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      4. {language === 'nl' ? 'Levering van Diensten' : 'Service Delivery'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <ul className="space-y-3 text-deep-purple/70 dark:text-cream/70">
                      <li>
                        {language === 'nl'
                          ? 'Agnus Cloud streeft naar het nakomen van afgesproken levertijden, maar deze zijn indicatief en geen harde deadlines.'
                          : 'Agnus Cloud strives to meet agreed delivery times, but these are indicative and not hard deadlines.'
                        }
                      </li>
                      <li>
                        {language === 'nl'
                          ? 'Vertraging in levering geeft de klant geen recht op schadevergoeding of ontbinding van de overeenkomst.'
                          : 'Delivery delays do not entitle the client to compensation or dissolution of the agreement.'
                        }
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 5. Aansprakelijkheid */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="p-3 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500"
                    >
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-deep-purple dark:text-cream">
                      5. {language === 'nl' ? 'Aansprakelijkheid' : 'Liability'}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <ul className="space-y-3 text-deep-purple/70 dark:text-cream/70">
                      <li>
                        {language === 'nl'
                          ? 'Agnus Cloud is niet aansprakelijk voor enige indirecte of gevolgschade, zoals winstderving of verlies van gegevens, tenzij sprake is van opzet of grove nalatigheid.'
                          : 'Agnus Cloud is not liable for any indirect or consequential damage, such as loss of profit or loss of data, unless there is intent or gross negligence.'
                        }
                      </li>
                      <li>
                        {language === 'nl'
                          ? 'De aansprakelijkheid van Agnus Cloud is beperkt tot het bedrag dat door de klant is betaald voor de betreffende dienst.'
                          : 'The liability of Agnus Cloud is limited to the amount paid by the client for the relevant service.'
                        }
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cloud-blue/5 to-cloud-lavender/5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-3xl font-bold text-deep-purple dark:text-cream mb-8">
            {language === 'nl' ? 'Vragen over deze voorwaarden?' : 'Questions about these terms?'}
          </h2>
          <p className="text-lg text-deep-purple/70 dark:text-cream/70 mb-8">
            {language === 'nl'
              ? 'Neem contact met ons op voor meer informatie.'
              : 'Contact us for more information.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-cloud-blue to-cloud-lavender hover:from-cloud-blue/90 hover:to-cloud-lavender/90 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Link href="mailto:info@agnuscloud.com" className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  info@agnuscloud.com
                </Link>
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                asChild
                size="lg"
                className="secondary-button px-8 py-6 text-lg font-semibold rounded-full"
              >
                <Link href="tel:+31854883543" className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  +31 85 - 488 354 3
                </Link>
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default TermsPage;
